<template>
  <div v-if="organization">
    <ShowPageHeader backLabel="Organisations" backRoute="/organizations" :title="organization.name">
      <template #badges>
        <FieldSpan :value="$t(organization.source)" :style-to-add="sourceBadgeStyle" />
      </template>
      <template #actions>
        <SecondaryButton @click="editOrganization">Modifier</SecondaryButton>
        <CreateButton @click="openStoreConfigurationModal = true">Nouveau point de vente</CreateButton>
        <CreateButton @click="createProfileModalOpen = true">Nouvel utilisateur</CreateButton>
        <SecondaryButton v-if="allowedToCreateMandate" @click="openInvoiceMandateSetupModal = true">
          Demander un mandat de prélèvement
        </SecondaryButton>
        <SecondaryButton v-if="isMandateToSetup(organization.mandate)" @click="openResendModal = true">
          Renvoyer le lien du mandat
        </SecondaryButton>
        <SecondaryButton
          v-if="isMandateToSetup(organization.mandate) || isMandateActive(organization.mandate)"
          @click="openCancelModal = true"
        >
          {{ $t(isMandateActive(organization.mandate) ? "mandate.cancel_button" : "mandate.cancel_request_button") }}
        </SecondaryButton>
      </template>
    </ShowPageHeader>

    <div class="mt-6">
      <GeneralInformations
        v-model:editMode="organizationEditMode"
        :organization="organization"
        @updated="fetchOrganization"
      />
    </div>

    <TabsNavigation :tabs="tabs" class="my-6" />

    <!-- Tab: Points de vente -->
    <div v-if="currentTab === 'stores'">
      <StoreList :organizationId="organizationId" :showSearchBar="false" />
    </div>

    <!-- Tab: Utilisateurs -->
    <div v-if="currentTab === 'users'">
      <MerchantProfilesList :organizationIds="[organizationId]" />
    </div>

    <!-- Tab: Entités de facturation -->
    <div v-if="currentTab === 'billing_entities'">
      <div class="flex justify-end mb-4">
        <CreateButton @click="createBillingEntityModalOpen = true">Nouvelle entité de facturation</CreateButton>
      </div>
      <BillingEntitiesList :organizationId="organizationId" :stores="organization.stores || []" />
    </div>

    <ConfigureStoreModal
      :organization-id="organizationId"
      :open-modal="openStoreConfigurationModal"
      @updateOrganization="fetchOrganization"
      @close="openStoreConfigurationModal = false"
    />
    <FullScreenModal :open="createProfileModalOpen" @close="createProfileModalOpen = false">
      <MerchantProfileFormNew :organizationId="organizationId" @success="newProfileCreated" />
    </FullScreenModal>
    <Modal :open="createBillingEntityModalOpen" @close="createBillingEntityModalOpen = false">
      <BillingEntityFormNew
        :organizationId="organizationId"
        :stores="organization.stores || []"
        @success="newBillingEntityCreated"
      />
    </Modal>
    <InvoiceMandateSetupModal
      :open="openInvoiceMandateSetupModal"
      @sendEmail="sendInvoiceMandateForm"
      @close="openInvoiceMandateSetupModal = false"
    />
    <ResendMandateLinkModal
      :open="openResendModal"
      :email="organization.mandate?.responsibleEmail"
      :loading="mandateActionLoading"
      @confirm="resendMandateLink"
      @close="openResendModal = false"
    />
    <CancelMandateModal
      :open="openCancelModal"
      :email="organization.mandate?.responsibleEmail"
      :active="isMandateActive(organization.mandate)"
      :loading="mandateActionLoading"
      @confirm="cancelMandateRequest"
      @close="openCancelModal = false"
    />
  </div>
</template>

<script>
import GeneralInformations from "@/views/organizations/details/GeneralInformations.vue";
import StoreList from "@/views/stores/StoreList.vue";
import ConfigureStoreModal from "@/views/organizations/ConfigureStoreModal.vue";
import MerchantProfilesList from "@/components/merchant_profiles/MerchantProfilesList.vue";
import MerchantProfileFormNew from "@/components/merchant_profiles/MerchantProfileFormNew.vue";
import BillingEntitiesList from "@/components/billing_entities/BillingEntitiesList.vue";
import BillingEntityFormNew from "@/components/billing_entities/BillingEntityFormNew.vue";
import { mapActions, mapGetters } from "vuex";
import {
  toCamelCase,
  CreateButton,
  FieldSpan,
  FullScreenModal,
  Modal,
  SecondaryButton,
  ShowPageHeader,
  TabsNavigation,
} from "@estaly/ui";
import InvoiceMandateSetupModal from "@/components/mandate/InvoiceMandateSetupModal.vue";
import CancelMandateModal from "@/components/mandate/CancelMandateModal.vue";
import ResendMandateLinkModal from "@/components/mandate/ResendMandateLinkModal.vue";

export default {
  components: {
    CreateButton,
    FieldSpan,
    FullScreenModal,
    Modal,
    SecondaryButton,
    ShowPageHeader,
    TabsNavigation,
    InvoiceMandateSetupModal,
    CancelMandateModal,
    ResendMandateLinkModal,
    GeneralInformations,
    StoreList,
    ConfigureStoreModal,
    MerchantProfilesList,
    MerchantProfileFormNew,
    BillingEntitiesList,
    BillingEntityFormNew,
  },

  async beforeMount() {
    this.organizationId = Number(this.$route.params.id);
    await this.fetchOrganization();
  },

  data() {
    return {
      organizationId: null,
      organization: null,
      openStoreConfigurationModal: false,
      openInvoiceMandateSetupModal: false,
      openResendModal: false,
      openCancelModal: false,
      mandateActionLoading: false,
      createProfileModalOpen: false,
      createBillingEntityModalOpen: false,
      organizationEditMode: false,
    };
  },

  computed: {
    ...mapGetters("mandates", ["isMandateToSetup", "isMandateActive"]),
    currentTab() {
      return this.$route.query.tab || "stores";
    },

    tabs() {
      const tabs = [
        { name: "stores", displayName: "Points de vente", query: { tab: "stores" } },
        { name: "users", displayName: "Utilisateurs", query: { tab: "users" } },
      ];
      if (this.isBillingEntityLevel) {
        tabs.push({
          name: "billing_entities",
          displayName: "Entités de facturation",
          query: { tab: "billing_entities" },
        });
      }
      return tabs;
    },

    allowedToCreateMandate() {
      return !this.organization?.mandate && this.organization?.invoiceLevel === "organization";
    },

    sourceBadgeStyle() {
      const styles = {
        web: "bg-blue-100 text-blue-800",
        retail: "bg-green-100 text-green-800",
        multichannel: "bg-purple-100 text-purple-800",
      };
      return styles[this.organization?.source] || "bg-gray-100 text-gray-800";
    },

    isBillingEntityLevel() {
      return this.organization?.invoiceLevel === "billing_entity";
    },
  },

  methods: {
    ...mapActions("backoffice", ["withLoader"]),
    ...mapActions("notifications", ["notify"]),
    ...mapActions("organizations", ["fetch"]),
    ...mapActions("merchantProfiles", { fetchMerchantProfiles: "fetchIndex" }),
    ...mapActions("billingEntities", { fetchBillingEntities: "fetchIndex" }),
    ...mapActions("mandates", {
      sendMandateForm: "sendInvoiceMandateForm",
      sendNewLink: "sendNewLink",
      cancelMandate: "cancel",
    }),

    editOrganization() {
      this.organizationEditMode = true;
    },

    newProfileCreated() {
      this.createProfileModalOpen = false;
      this.fetchMerchantProfiles({ organizationIds: [this.organizationId] });
    },

    newBillingEntityCreated() {
      this.createBillingEntityModalOpen = false;
      this.fetchBillingEntities({ organizationId: this.organizationId });
    },

    async fetchOrganization() {
      if (!this.organizationId) return;

      await this.withLoader(async () => {
        const response = await this.fetch({
          id: this.organizationId,
          includes: ["stores", "stores.setup_by", "mandate"],
        });
        if (response?.success) this.organization = toCamelCase(response.data);
      });
    },

    async sendInvoiceMandateForm(email) {
      const { success } = await this.sendMandateForm({
        mandateSubjectType: "Organization",
        mandateSubjectId: this.organizationId,
        email,
      });
      if (!success) return;
      this.openInvoiceMandateSetupModal = false;
      await this.fetchOrganization();
    },

    async resendMandateLink() {
      if (this.mandateActionLoading) return;
      this.mandateActionLoading = true;
      try {
        const { success } = await this.sendNewLink({ mandateId: this.organization.mandate.id });
        if (!success) return;
        this.openResendModal = false;
        await this.fetchOrganization();
      } finally {
        this.mandateActionLoading = false;
      }
    },

    async cancelMandateRequest() {
      if (this.mandateActionLoading) return;
      this.mandateActionLoading = true;
      try {
        const { success } = await this.cancelMandate({ mandateId: this.organization.mandate.id });
        if (!success) return;
        this.openCancelModal = false;
        this.openResendModal = false;
        await this.fetchOrganization();
      } finally {
        this.mandateActionLoading = false;
      }
    },
  },
};
</script>
